/* SuggestedTemplatesCard — contextual template nudge on the Documents page.
 *
 * Surfaces 2-3 industry-matched folder templates the user hasn't applied yet.
 * One-click "Apply" calls the existing /setup-apply endpoint with a single
 * template_id; the card hides that template (and the whole card if none
 * remain). 14-day dismiss memory in localStorage.
 *
 * Designed to be a small, non-intrusive strip — not a full BrandEmptyState.
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { track } from '../lib/telemetry';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sparkles, ArrowRight, X, FolderTree, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_TTL_DAYS = 14;
const MAX_SHOWN = 3;


function dismissKey(wsId) {
  return `suggestedTemplatesDismissed_${wsId}`;
}


function wasDismissedRecently(wsId) {
  try {
    const ts = parseInt(localStorage.getItem(dismissKey(wsId)) || '0', 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
  } catch (_e) { return false; }
}


function appliedKey(wsId, templateId) {
  return `appliedTemplate_${wsId}_${templateId}`;
}


export default function SuggestedTemplatesCard({ existingFolderNames = [], onApplied }) {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [suggestions, setSuggestions] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [applying, setApplying] = useState(null);
  const [appliedIds, setAppliedIds] = useState(() => new Set());
  const trackedShown = useRef(false);

  // Load suggestions on workspace change
  useEffect(() => {
    if (!wsId) return;
    if (wasDismissedRecently(wsId)) { setHidden(true); return; }
    setHidden(false);
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/setup-suggestions`);
        if (r.ok) {
          const data = await r.json();
          setSuggestions(data.suggestions || []);
        }
      } catch (_e) { /* silent */ }
    })();
  }, [wsId]);

  // Filter out: templates the user already applied (per localStorage marker),
  // and templates whose name matches a folder that already exists.
  const visibleSuggestions = useMemo(() => {
    if (!wsId) return [];
    const lowercaseFolders = new Set(
      (existingFolderNames || []).map((n) => (n || '').toLowerCase().trim())
    );
    return suggestions
      .filter((s) => !appliedIds.has(s.template_id))
      .filter((s) => {
        try { return !localStorage.getItem(appliedKey(wsId, s.template_id)); }
        catch (_e) { return true; }
      })
      .filter((s) => !lowercaseFolders.has((s.name || '').toLowerCase().trim()))
      .slice(0, MAX_SHOWN);
  }, [suggestions, appliedIds, existingFolderNames, wsId]);

  if (hidden || !wsId || visibleSuggestions.length === 0) return null;

  // Fire 'shown' once per visible session per workspace
  if (!trackedShown.current) {
    trackedShown.current = true;
    track('template_card.shown', { count: visibleSuggestions.length }, wsId);
  }

  const applyTemplate = async (template) => {
    if (!template?.template_id) return;
    setApplying(template.template_id);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/setup-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_ids: [template.template_id] }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Could not apply template');
        return;
      }
      try { localStorage.setItem(appliedKey(wsId, template.template_id), String(Date.now())); }
      catch (_e) { /* silent */ }
      setAppliedIds((prev) => {
        const next = new Set(prev);
        next.add(template.template_id);
        return next;
      });
      track('template_card.applied', {
        template_id: template.template_id,
        template_name: template.name,
        industry: template.industry,
      }, wsId);
      toast.success(`Applied "${template.name}" — folders ready in your document tree.`);
      onApplied?.();
    } catch (e) {
      toast.error('Could not apply template');
    }
    setApplying(null);
  };

  const dismiss = () => {
    track('template_card.dismissed', { remaining_count: visibleSuggestions.length }, wsId);
    try { localStorage.setItem(dismissKey(wsId), String(Date.now())); } catch (_e) { /* silent */ }
    setHidden(true);
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-violet-500/25 bg-gradient-to-br from-violet-500/8 via-emerald-500/4 to-blue-500/6 dark:from-violet-500/10 dark:via-emerald-500/6 dark:to-blue-500/8 p-4"
      data-testid="suggested-templates-card"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.025)_1px,transparent_0)] dark:bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.03)_1px,transparent_0)] [background-size:24px_24px] pointer-events-none" />

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss for 14 days"
        title="Dismiss for 14 days"
        className="absolute top-2.5 right-2.5 p-1 rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        data-testid="suggested-templates-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="relative flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-md shadow-violet-500/15">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-500 mb-0.5">
            Suggested for you
          </p>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
            Templates that{' '}
            <span className="bg-gradient-to-r from-violet-500 to-emerald-500 bg-clip-text text-transparent">
              fit your workspace.
            </span>
          </h3>
        </div>
      </div>

      <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {visibleSuggestions.map((s) => {
          const isApplying = applying === s.template_id;
          return (
            <div
              key={s.template_id}
              className={cn(
                'flex flex-col gap-2 p-3 rounded-lg border border-zinc-200 dark:border-white/10 bg-white/60 dark:bg-zinc-950/40 backdrop-blur',
                'hover:border-violet-500/40 transition-colors',
              )}
              data-testid={`suggested-template-${s.template_id}`}
            >
              <div className="flex items-start gap-2">
                <FolderTree className="w-4 h-4 text-violet-500 dark:text-violet-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                    {s.name}
                  </p>
                  <p className="text-[11px] text-zinc-500 dark:text-zinc-500 line-clamp-2 leading-snug mt-0.5">
                    {s.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-auto">
                <div className="flex gap-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">
                    {s.node_count} folders
                  </Badge>
                  {s.industry && (
                    <Badge variant="outline" className="text-[9px] font-mono px-1.5 py-0">
                      {s.industry}
                    </Badge>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => applyTemplate(s)}
                  disabled={isApplying}
                  className="h-7 px-2.5 text-xs bg-emerald-500 hover:bg-emerald-400 text-black font-semibold gap-1"
                  data-testid={`suggested-template-apply-${s.template_id}`}
                >
                  {isApplying
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <>Apply <ArrowRight className="w-3 h-3" /></>}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
